import React, { useState, useEffect } from 'react';
import { 
  PenTool, Download, FileText, Ruler, Box, 
  Circle, Square, Triangle, Settings, Loader2,
  RefreshCw, ChevronDown, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

const CAD_PRODUCTS = [
  { id: 'fire_damper', name_ar: 'دامبر حريق', name_en: 'Fire Damper', icon: Box, color: 'text-red-500' },
  { id: 'access_panel', name_ar: 'لوحة فحص', name_en: 'Access Panel', icon: Square, color: 'text-blue-500' },
  { id: 'smoke_damper', name_ar: 'دامبر دخان', name_en: 'Smoke Damper', icon: Box, color: 'text-orange-500' },
  { id: 'rectangular_duct', name_ar: 'مجرى هواء مستطيل', name_en: 'Rectangular Duct', icon: Square, color: 'text-green-500' },
  { id: 'round_duct', name_ar: 'مجرى هواء دائري', name_en: 'Round Duct', icon: Circle, color: 'text-cyan-500' },
  { id: 'duct_elbow', name_ar: 'كوع مجرى', name_en: 'Duct Elbow', icon: Triangle, color: 'text-purple-500' },
  { id: 'transition', name_ar: 'محول مجرى', name_en: 'Transition', icon: Triangle, color: 'text-yellow-500' },
];

const DEFAULT_DIMS = {
  fire_damper: { width: 400, height: 400, depth: 300, flange_width: 25, blade_count: 3 },
  access_panel: { width: 300, height: 300, depth: 50, frame_width: 30 },
  smoke_damper: { width: 500, height: 500, depth: 350 },
  rectangular_duct: { width: 600, height: 400, length: 1200 },
  round_duct: { diameter: 300, length: 1000 },
  duct_elbow: { width: 400, height: 400, radius: 200, angle: 90 },
  transition: { width1: 600, height1: 400, width2: 400, height2: 300, length: 400 },
};

const DIMENSION_LABELS = {
  width: 'العرض / Width',
  height: 'الارتفاع / Height',
  depth: 'العمق / Depth',
  length: 'الطول / Length',
  diameter: 'القطر / Diameter',
  radius: 'نصف القطر / Radius',
  angle: 'الزاوية / Angle',
  flange_width: 'عرض الفلنجة / Flange Width',
  blade_count: 'عدد الشفرات / Blade Count',
  frame_width: 'عرض الإطار / Frame Width',
  width1: 'العرض 1 / Width 1',
  height1: 'الارتفاع 1 / Height 1',
  width2: 'العرض 2 / Width 2',
  height2: 'الارتفاع 2 / Height 2',
};

export default function CadStudioPage() {
  const [selectedProduct, setSelectedProduct] = useState('fire_damper');
  const [dimensions, setDimensions] = useState(DEFAULT_DIMS.fire_damper);
  const [loading, setLoading] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [recentFiles, setRecentFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState('both');

  useEffect(() => {
    loadRecentFiles();
  }, []);

  useEffect(() => {
    setDimensions(DEFAULT_DIMS[selectedProduct] || {});
    setGeneratedFiles(null);
  }, [selectedProduct]);

  const loadRecentFiles = async () => {
    try {
      const response = await api.get('/cad/files');
      if (response.data.success) {
        setRecentFiles(response.data.files.slice(0, 10));
      }
    } catch (error) {
      console.error('Error loading recent files:', error);
    }
  };

  const handleDimensionChange = (key, value) => {
    setDimensions(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  const generateCAD = async () => {
    setLoading(true);
    try {
      const response = await api.post('/cad/generate', {
        product_type: selectedProduct,
        dimensions: dimensions,
        output_format: outputFormat
      });

      if (response.data.success) {
        setGeneratedFiles(response.data);
        toast.success('تم إنشاء الرسم بنجاح! / Drawing generated successfully!');
        loadRecentFiles();
      } else {
        toast.error(response.data.error || 'فشل في إنشاء الرسم');
      }
    } catch (error) {
      console.error('Error generating CAD:', error);
      toast.error('حدث خطأ أثناء إنشاء الرسم');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (filename) => {
    try {
      const response = await api.get(`/cad/download/${filename}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`تم تحميل ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('فشل في تحميل الملف');
    }
  };

  const selectedProductInfo = CAD_PRODUCTS.find(p => p.id === selectedProduct);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <PenTool className="w-10 h-10 text-amber-400" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              استوديو CAD
            </h1>
          </div>
          <p className="text-slate-400 text-lg">
            إنشاء رسومات CAD احترافية لمنتجات DHR
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Product Selection */}
          <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Box className="w-5 h-5 text-amber-400" />
                اختر المنتج
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {CAD_PRODUCTS.map((product) => {
                const Icon = product.icon;
                const isSelected = selectedProduct === product.id;
                return (
                  <button
                    key={product.id}
                    onClick={() => setSelectedProduct(product.id)}
                    className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all ${
                      isSelected 
                        ? 'bg-amber-500/20 border-2 border-amber-500' 
                        : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700'
                    }`}
                    data-testid={`cad-product-${product.id}`}
                  >
                    <Icon className={`w-5 h-5 ${product.color}`} />
                    <div className="text-right flex-1">
                      <div className="text-white font-medium">{product.name_ar}</div>
                      <div className="text-slate-400 text-sm">{product.name_en}</div>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-amber-400" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Dimensions Input */}
          <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Ruler className="w-5 h-5 text-amber-400" />
                الأبعاد (مم)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.keys(dimensions).map((key) => (
                <div key={key} className="space-y-1">
                  <Label className="text-slate-300 text-sm">
                    {DIMENSION_LABELS[key] || key}
                  </Label>
                  <Input
                    type="number"
                    value={dimensions[key]}
                    onChange={(e) => handleDimensionChange(key, e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid={`cad-dim-${key}`}
                  />
                </div>
              ))}

              {/* Output Format */}
              <div className="pt-4 border-t border-slate-700">
                <Label className="text-slate-300 text-sm mb-2 block">
                  صيغة الإخراج / Output Format
                </Label>
                <div className="flex gap-2">
                  {[
                    { id: 'dxf', label: 'DXF فقط' },
                    { id: 'pdf', label: 'PDF فقط' },
                    { id: 'both', label: 'كلاهما' },
                  ].map((format) => (
                    <button
                      key={format.id}
                      onClick={() => setOutputFormat(format.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                        outputFormat === format.id
                          ? 'bg-amber-500 text-black font-medium'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {format.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={generateCAD}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold py-6 text-lg"
                data-testid="cad-generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 ml-2 animate-spin" />
                    جاري الإنشاء...
                  </>
                ) : (
                  <>
                    <PenTool className="w-5 h-5 ml-2" />
                    إنشاء الرسم
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results & Downloads */}
          <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-amber-400" />
                الملفات المنشأة
              </CardTitle>
            </CardHeader>
            <CardContent>
              {generatedFiles ? (
                <div className="space-y-4">
                  {/* Product Info */}
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {selectedProductInfo && (
                        <selectedProductInfo.icon className={`w-6 h-6 ${selectedProductInfo.color}`} />
                      )}
                      <span className="text-white font-bold">
                        {generatedFiles.product_code}
                      </span>
                    </div>
                    <div className="text-slate-400 text-sm">
                      {Object.entries(generatedFiles.dimensions || {}).map(([k, v]) => (
                        <span key={k} className="ml-3">{k}: {v}mm</span>
                      ))}
                    </div>
                  </div>

                  {/* Download Buttons */}
                  <div className="space-y-2">
                    {generatedFiles.dxf_file && (
                      <Button
                        onClick={() => downloadFile(generatedFiles.dxf_file)}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="cad-download-dxf"
                      >
                        <FileText className="w-4 h-4 ml-2" />
                        تحميل DXF (AutoCAD)
                      </Button>
                    )}
                    {generatedFiles.pdf_file && (
                      <Button
                        onClick={() => downloadFile(generatedFiles.pdf_file)}
                        className="w-full bg-red-600 hover:bg-red-700"
                        data-testid="cad-download-pdf"
                      >
                        <FileText className="w-4 h-4 ml-2" />
                        تحميل PDF
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <PenTool className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>اختر المنتج والأبعاد ثم اضغط "إنشاء الرسم"</p>
                </div>
              )}

              {/* Recent Files */}
              {recentFiles.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <h4 className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    الملفات الأخيرة
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {recentFiles.map((file, idx) => (
                      <button
                        key={idx}
                        onClick={() => downloadFile(file.filename)}
                        className="w-full p-2 rounded bg-slate-700/50 hover:bg-slate-700 text-right flex items-center justify-between"
                      >
                        <span className="text-slate-300 text-sm truncate flex-1">
                          {file.filename}
                        </span>
                        <Download className="w-4 h-4 text-slate-400 mr-2" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card className="bg-slate-800/30 border-slate-700 mt-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-6 justify-center text-slate-400 text-sm">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>متوافق مع AutoCAD</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>رسومات تقنية احترافية</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>قوالب منتجات DHR</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <span>مجاني 100%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
